import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import useAxios from '../api/axios';
import useAuth from '../hooks/useAuth';

const getCurrentUserID = (auth) => {
  const possibleUserID = auth?.user?.userID
    ?? auth?.user?.ID
    ?? auth?.user?.userObj?.userID
    ?? auth?.user?.userObj?.ID
    ?? null;

  return possibleUserID !== null && possibleUserID !== undefined
    ? Number(possibleUserID)
    : null;
};

const hasTimestampValue = (value) => typeof value === 'string'
  ? value.trim() !== '' && value !== '0000-00-00 00:00:00'
  : Boolean(value);

const formatDateTime = (value) => {
  if (!hasTimestampValue(value)) {
    return 'Ei tiedossa';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleString('fi-FI', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const hasElapsedHours = (value, hours) => {
  if (!hasTimestampValue(value)) {
    return false;
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return false;
  }

  return Date.now() - date.getTime() >= hours * 60 * 60 * 1000;
};

const getAutoConfirmedStatusLabel = (isConfirmed, isAutoConfirmed) => {
  if (!isConfirmed) {
    return 'odottaa';
  }

  return isAutoConfirmed ? 'automaattinen' : 'vahvistettu';
};

const getPairUserID = (pair, slot) => Number(pair?.[`userID${slot}`] ?? pair?.[`metrixUserID${slot}`] ?? -1);

const getPlayerName = (pair, slot) => {
  const name = pair?.[`user${slot}`];
  if (name && String(name).trim() !== '') {
    return name;
  }

  const userID = getPairUserID(pair, slot);
  return userID > 0 ? `Pelaaja #${userID}` : `Kilpailija ${slot}`;
};

const getHoleResults = (pair) => {
  if (Array.isArray(pair?.holeResults)) {
    return pair.holeResults;
  }

  return [];
};

const sortHoleResults = (entries) => [...entries].sort((left, right) => {
  const leftHole = left?.hole;
  const rightHole = right?.hole;

  if (leftHole === 'SD') {
    return 1;
  }

  if (rightHole === 'SD') {
    return -1;
  }

  return Number(leftHole ?? 0) - Number(rightHole ?? 0);
});

const upsertHoleResult = (entries, nextEntry) => sortHoleResults([
  ...entries.filter((entry) => String(entry?.hole) !== String(nextEntry?.hole)),
  nextEntry,
]);

const resolveWinnerFromResults = (pair, holeResults) => {
  const player1UserID = getPairUserID(pair, 1);
  const player2UserID = getPairUserID(pair, 2);

  let player1Points = 0;
  let player2Points = 0;
  let hasHole18 = false;
  let playedRegulationHoles = 0;
  let winnerUserID = 0;

  holeResults.forEach((entry) => {
    const entryHole = entry?.hole;
    const entryWinnerUserID = Number(entry?.winnerUserID ?? -1);

    if (entryHole !== 'SD') {
      playedRegulationHoles += 1;
    }

    if (String(entryHole) === '18') {
      hasHole18 = true;
    }

    if (entryWinnerUserID === player1UserID) {
      player1Points += 1;
    }

    if (entryWinnerUserID === player2UserID) {
      player2Points += 1;
    }

    if (entryHole === 'SD' && entryWinnerUserID !== -1) {
      winnerUserID = entryWinnerUserID;
    }
  });

  if (winnerUserID === 0) {
    const remainingRegulationHoles = Math.max(18 - playedRegulationHoles, 0);
    const lead = Math.abs(player1Points - player2Points);

    if (lead > remainingRegulationHoles) {
      winnerUserID = player1Points > player2Points ? player1UserID : player2UserID;
    }
  }

  if (winnerUserID === 0 && hasHole18 && player1Points !== player2Points) {
    winnerUserID = player1Points > player2Points ? player1UserID : player2UserID;
  }

  return winnerUserID;
};

const applyPendingScoresToPair = (pair, pendingScores) => {
  if (!pair) {
    return pair;
  }

  const mergedHoleResults = Array.isArray(pendingScores) && pendingScores.length > 0
    ? pendingScores.reduce(
      (entries, pendingEntry) => upsertHoleResult(entries, pendingEntry),
      getHoleResults(pair)
    )
    : getHoleResults(pair);

  return {
    ...pair,
    holeResults: mergedHoleResults,
    winnerUserID: resolveWinnerFromResults(pair, mergedHoleResults),
  };
};

const getSelectedHoleWinner = (pair, hole) => {
  if (!pair || hole === null || hole === undefined) {
    return null;
  }

  const selectedEntry = getHoleResults(pair).find((entry) => String(entry?.hole) === String(hole));
  return selectedEntry ? Number(selectedEntry.winnerUserID ?? -1) : null;
};

const getUnifiedHoleBadgeTone = (winnerUserID, player1UserID, player2UserID) => {
  if (winnerUserID === player1UserID) {
    return 'bg-cyan-600 text-white border-cyan-500';
  }

  if (winnerUserID === player2UserID) {
    return 'bg-slate-900 text-white border-slate-700';
  }

  if (winnerUserID === -1) {
    return 'bg-amber-400 text-slate-950 border-amber-500';
  }

  return 'bg-white text-slate-500 border-slate-300';
};

const calculatePoints = (pair) => {
  const player1UserID = getPairUserID(pair, 1);
  const player2UserID = getPairUserID(pair, 2);

  return getHoleResults(pair).reduce((accumulator, entry) => {
    const winnerUserID = Number(entry?.winnerUserID ?? -1);

    if (winnerUserID === player1UserID) {
      return { ...accumulator, player1: accumulator.player1 + 1 };
    }

    if (winnerUserID === player2UserID) {
      return { ...accumulator, player2: accumulator.player2 + 1 };
    }

    return accumulator;
  }, { player1: 0, player2: 0 });
};

const getNextEditableHole = (pair) => {
  const holeResults = getHoleResults(pair);
  const numericHoles = holeResults
    .map((entry) => (entry?.hole === 'SD' ? null : Number(entry?.hole ?? null)))
    .filter((hole) => hole !== null && !Number.isNaN(hole));

  const maxNumericHole = numericHoles.length > 0 ? Math.max(...numericHoles) : 0;
  const hasSuddenDeathResult = holeResults.some((entry) => entry?.hole === 'SD');
  const points = calculatePoints(pair);
  const winner = Number(pair?.winnerUserID ?? 0);
  const remainingRegulationHoles = Math.max(18 - numericHoles.length, 0);
  const lead = Math.abs(points.player1 - points.player2);

  if (winner !== 0 || hasSuddenDeathResult) {
    return null;
  }

  if (lead > remainingRegulationHoles) {
    return null;
  }

  if (maxNumericHole < 18) {
    return maxNumericHole + 1;
  }

  if (points.player1 === points.player2) {
    return 'SD';
  }

  return null;
};

const buildVisibleHoles = (pair) => {
  const nextHole = getNextEditableHole(pair);
  const holeResults = getHoleResults(pair);
  const numericHoles = holeResults
    .map((entry) => (entry?.hole === 'SD' ? null : Number(entry?.hole ?? null)))
    .filter((hole) => hole !== null && !Number.isNaN(hole));
  const maxNumericHole = numericHoles.length > 0 ? Math.max(...numericHoles) : 0;
  const numericSequenceEnd = nextHole === 'SD' || nextHole === null ? Math.min(Math.max(maxNumericHole, 18), 18) : Math.max(maxNumericHole, Number(nextHole));
  const visibleHoles = Array.from({ length: numericSequenceEnd }, (_, index) => index + 1);

  if (nextHole === 'SD' || holeResults.some((entry) => entry?.hole === 'SD')) {
    visibleHoles.push('SD');
  }

  if (visibleHoles.length === 0) {
    visibleHoles.push(1);
  }

  return visibleHoles;
};

const parseCompetitions = (data) => {
  if (Array.isArray(data)) {
    return data;
  }

  if (Array.isArray(data?.competitions)) {
    return data.competitions;
  }

  return [];
};

const pairIDsMatch = (left, right) => String(left ?? '') === String(right ?? '');

const getHoleSortValue = (hole) => (String(hole) === 'SD' ? Number.MAX_SAFE_INTEGER : Number(hole ?? 0));

const hashValue = (value) => String(value ?? '').split('').reduce((accumulator, character) => {
  const nextValue = ((accumulator << 5) - accumulator) + character.charCodeAt(0);
  return nextValue | 0;
}, 0);

const getInitialStarterUserID = (pair) => {
  if (!pair) {
    return null;
  }

  const player1UserID = getPairUserID(pair, 1);
  const player2UserID = getPairUserID(pair, 2);

  if (player1UserID <= 0 || player2UserID <= 0) {
    return player1UserID > 0 ? player1UserID : player2UserID;
  }

  return Math.abs(hashValue(pair?.pairID ?? pair?.competitionID ?? `${player1UserID}-${player2UserID}`)) % 2 === 0
    ? player1UserID
    : player2UserID;
};

const getStarterUserIDForHole = (pair, hole) => {
  if (!pair || hole === null || hole === undefined) {
    return null;
  }

  let starterUserID = getInitialStarterUserID(pair);
  const player1UserID = getPairUserID(pair, 1);
  const player2UserID = getPairUserID(pair, 2);
  const holeResults = sortHoleResults(getHoleResults(pair));

  for (const entry of holeResults) {
    if (String(entry?.hole) === String(hole)) {
      break;
    }

    const winnerUserID = Number(entry?.winnerUserID ?? -1);
    if (winnerUserID === player1UserID || winnerUserID === player2UserID) {
      starterUserID = winnerUserID;
    }
  }

  return starterUserID;
};

const allHoleLabels = [...Array.from({ length: 18 }, (_, index) => index + 1), 'SD'];

export default function ScoreEntry() {
  const { competitionID, pairID } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const api = useAxios();
  const { auth } = useAuth();

  const initialCompetition = location.state?.competition ?? null;
  const initialPair = location.state?.pair ?? null;
  const [competition, setCompetition] = useState(initialCompetition);
  const [pair, setPair] = useState(initialPair);
  const [selectedHole, setSelectedHole] = useState(initialPair ? getNextEditableHole(initialPair) ?? 1 : 1);
  const [loading, setLoading] = useState(!initialCompetition || !initialPair);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [pendingScores, setPendingScores] = useState([]);
  const [isEditingCompletedResult, setIsEditingCompletedResult] = useState(false);

  useEffect(() => {
    if (initialCompetition && initialPair) {
      return;
    }

    let cancelled = false;

    const loadData = async () => {
      setLoading(true);
      setError('');

      try {
        const [competitionsResponse, pairsResponse] = await Promise.all([
          api.post('/getCompetitions', JSON.stringify({}), { headers: { 'Content-Type': 'application/json' } }),
          api.post('/getPairs', JSON.stringify({ competitionID: Number(competitionID) }), { headers: { 'Content-Type': 'application/json' } }),
        ]);

        if (cancelled) {
          return;
        }

        const competitions = parseCompetitions(competitionsResponse?.data);
        const currentCompetition = competitions.find((item) => Number(item?.competitionID) === Number(competitionID)) ?? null;
        const allPairs = Array.isArray(pairsResponse?.data) ? pairsResponse.data : [];
        const currentPair = allPairs.find((item) => pairIDsMatch(item?.pairID, pairID)) ?? null;

        setCompetition(currentCompetition);
        setPair(currentPair);

        if (!currentPair) {
          setError('Otteluparia ei löytynyt.');
        }
      } catch (requestError) {
        if (!cancelled) {
          setError(requestError?.response?.data?.msg || 'Kirjaussivun lataus epäonnistui.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, [api, competitionID, initialCompetition, initialPair, pairID]);

  const effectivePair = useMemo(() => applyPendingScoresToPair(pair, pendingScores), [pair, pendingScores]);
  const holeSequence = useMemo(() => (effectivePair ? buildVisibleHoles(effectivePair) : [1]), [effectivePair]);
  const points = useMemo(() => (effectivePair ? calculatePoints(effectivePair) : { player1: 0, player2: 0 }), [effectivePair]);
  const nextEditableHole = useMemo(() => (effectivePair ? getNextEditableHole(effectivePair) : 1), [effectivePair]);
  const isSummaryVisible = effectivePair ? nextEditableHole === null : false;
  const player1Name = useMemo(() => getPlayerName(effectivePair, 1), [effectivePair]);
  const player2Name = useMemo(() => getPlayerName(effectivePair, 2), [effectivePair]);
  const player1UserID = useMemo(() => getPairUserID(effectivePair, 1), [effectivePair]);
  const player2UserID = useMemo(() => getPairUserID(effectivePair, 2), [effectivePair]);
  const selectedHoleStarterUserID = useMemo(() => getStarterUserIDForHole(effectivePair, selectedHole), [effectivePair, selectedHole]);
  const selectedHoleWinnerUserID = useMemo(() => getSelectedHoleWinner(effectivePair, selectedHole), [effectivePair, selectedHole]);
  const currentUserID = useMemo(() => getCurrentUserID(auth), [auth]);
  const currentUserSlot = useMemo(() => {
    if (currentUserID === null || currentUserID === undefined) {
      return null;
    }

    if (player1UserID === currentUserID) {
      return 1;
    }

    if (player2UserID === currentUserID) {
      return 2;
    }

    return null;
  }, [currentUserID, player1UserID, player2UserID]);
  const user1Confirmed = useMemo(() => hasTimestampValue(pair?.user1Comfirmed), [pair]);
  const user2Confirmed = useMemo(() => hasTimestampValue(pair?.user2Comfirmed), [pair]);
  const user1AutoConfirmed = useMemo(() => hasTimestampValue(pair?.user1AutoConfirmedAt), [pair]);
  const user2AutoConfirmed = useMemo(() => hasTimestampValue(pair?.user2AutoConfirmedAt), [pair]);
  const bothConfirmed = user1Confirmed && user2Confirmed;
  const autoConfirmed = useMemo(() => hasTimestampValue(pair?.autoConfirmedAt), [pair]);
  const autoConfirmedPlayersLabel = useMemo(() => {
    const players = [];

    if (user1AutoConfirmed) {
      players.push(player1Name);
    }

    if (user2AutoConfirmed) {
      players.push(player2Name);
    }

    return players.join(' ja ');
  }, [player1Name, player2Name, user1AutoConfirmed, user2AutoConfirmed]);
  const editWindowExpired = useMemo(() => hasElapsedHours(pair?.ended, 1), [pair]);
  const currentUserConfirmed = useMemo(() => {
    if (currentUserSlot === 1) {
      return user1Confirmed;
    }

    if (currentUserSlot === 2) {
      return user2Confirmed;
    }

    return false;
  }, [currentUserSlot, user1Confirmed, user2Confirmed]);

  const scoredHoleBadges = useMemo(() => getHoleResults(effectivePair), [effectivePair]);
  const unifiedHoleBadges = useMemo(() => {
    const resultMap = new Map(
      scoredHoleBadges.map((entry) => [String(entry?.hole), Number(entry?.winnerUserID ?? -9999)])
    );

    return allHoleLabels.map((hole) => ({
      hole,
      winnerUserID: resultMap.has(String(hole)) ? resultMap.get(String(hole)) : null,
    }));
  }, [scoredHoleBadges]);
  const frontNineBadges = useMemo(() => unifiedHoleBadges.filter((entry) => typeof entry.hole === 'number' && entry.hole >= 1 && entry.hole <= 9), [unifiedHoleBadges]);
  const backNineBadges = useMemo(() => unifiedHoleBadges.filter((entry) => typeof entry.hole === 'number' && entry.hole >= 10 && entry.hole <= 18), [unifiedHoleBadges]);
  const suddenDeathBadge = useMemo(() => unifiedHoleBadges.find((entry) => entry.hole === 'SD') ?? null, [unifiedHoleBadges]);
  const summaryRows = useMemo(() => {
    let runningPlayer1Points = 0;
    let runningPlayer2Points = 0;

    return getHoleResults(effectivePair).map((entry) => {
      const winnerUserID = Number(entry?.winnerUserID ?? -1);

      if (winnerUserID === player1UserID) {
        runningPlayer1Points += 1;
      }

      if (winnerUserID === player2UserID) {
        runningPlayer2Points += 1;
      }

      return {
        hole: entry?.hole,
        holeLabel: entry?.hole === 'SD' ? 'Suddendeath' : `Väylä ${entry?.hole}`,
        winnerUserID,
        player1Points: runningPlayer1Points,
        player2Points: runningPlayer2Points,
      };
    });
  }, [effectivePair, player1UserID, player2UserID]);

  useEffect(() => {
    if (!effectivePair) {
      return;
    }

    const targetHole = nextEditableHole ?? holeSequence[holeSequence.length - 1] ?? 1;
    setSelectedHole(targetHole);
  }, [effectivePair, holeSequence, nextEditableHole]);

  useEffect(() => {
    if (!isSummaryVisible || bothConfirmed) {
      setIsEditingCompletedResult(false);
    }
  }, [bothConfirmed, isSummaryVisible]);

  const showSummary = isSummaryVisible && !isEditingCompletedResult;

  const handleArrowNavigation = (direction) => {
    const currentIndex = holeSequence.findIndex((hole) => String(hole) === String(selectedHole));
    if (currentIndex === -1) {
      return;
    }

    const nextIndex = currentIndex + direction;
    if (nextIndex < 0 || nextIndex >= holeSequence.length) {
      return;
    }

    setSelectedHole(holeSequence[nextIndex]);
  };

  const handleSetScore = async (winnerUserID) => {
    if (!effectivePair || submitting || bothConfirmed || editWindowExpired) {
      return;
    }

    if (selectedHole === 'SD' && Number(winnerUserID) === -1) {
      return;
    }

    const nextEntry = {
      hole: selectedHole,
      winnerUserID: Number(winnerUserID),
    };

    const nextPendingScores = upsertHoleResult(pendingScores, nextEntry);
    const sortedScoresToSync = [...nextPendingScores].sort(
      (left, right) => getHoleSortValue(left.hole) - getHoleSortValue(right.hole)
    );

    setPendingScores(nextPendingScores);
    setSubmitting(true);
    setError('');

    try {
      let latestPair = pair;
      const syncedHoleKeys = [];

      for (const scoreEntry of sortedScoresToSync) {
        const response = await api.post(
          '/setScore',
          JSON.stringify({
            pairID: String(latestPair?.pairID ?? effectivePair.pairID),
            hole: scoreEntry.hole,
            winnerUserID: Number(scoreEntry.winnerUserID),
          }),
          { headers: { 'Content-Type': 'application/json' } }
        );

        const updatedPair = response?.data?.pair ?? null;
        if (updatedPair) {
          latestPair = updatedPair;
        }

        syncedHoleKeys.push(String(scoreEntry.hole));
      }

      if (latestPair) {
        setPair(latestPair);
        if (Number(latestPair?.winnerUserID ?? 0) !== 0) {
          setIsEditingCompletedResult(false);
        }
      }

      setPendingScores((currentScores) => currentScores.filter((entry) => !syncedHoleKeys.includes(String(entry.hole))));
    } catch (requestError) {
      const message = requestError?.response?.data?.msg || 'Tulosten tallennus epäonnistui. Tulokset yritetään lähettää uudelleen seuraavan väylän yhteydessä.';
      setError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditCompletedResult = () => {
    if (bothConfirmed || !effectivePair || editWindowExpired) {
      return;
    }

    const holeResults = getHoleResults(effectivePair);
    const lastHole = holeResults.length > 0 ? holeResults[holeResults.length - 1]?.hole ?? 18 : 18;

    setSelectedHole(lastHole);
    setIsEditingCompletedResult(true);
    setError('');
  };

  const handleConfirmScore = async () => {
    if (!pair || submitting || bothConfirmed || currentUserConfirmed || pendingScores.length > 0) {
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const response = await api.post(
        '/confirmScore',
        JSON.stringify({ pairID: String(pair.pairID) }),
        { headers: { 'Content-Type': 'application/json' } }
      );

      const updatedPair = response?.data?.pair ?? null;
      if (updatedPair) {
        setPair(updatedPair);
      }

      toast.success(response?.data?.bothConfirmed ? 'Molemmat pelaajat vahvistivat tuloksen.' : 'Tulos vahvistettu.');
    } catch (requestError) {
      const message = requestError?.response?.data?.msg || 'Tuloksen vahvistus epäonnistui.';
      setError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const summaryWinnerText = useMemo(() => {
    if (!effectivePair) {
      return '';
    }

    const winner = Number(effectivePair?.winnerUserID ?? 0);
    if (winner === player1UserID) {
      return `${player1Name} voitti`;
    }

    if (winner === player2UserID) {
      return `${player2Name} voitti`;
    }

    return 'Ottelu on kesken';
  }, [effectivePair, player1Name, player1UserID, player2Name, player2UserID]);

  if (loading) {
    return (
      <section className="mx-auto flex min-h-screen max-w-4xl items-center justify-center px-4 py-10">
        <div className="rounded-[28px] border border-slate-200 bg-white px-8 py-10 text-center shadow-[0_20px_70px_-30px_rgba(15,23,42,0.45)]">
          <p className="text-lg font-semibold text-slate-900">Ladataan kirjausta...</p>
        </div>
      </section>
    );
  }

  if (!pair) {
    return (
      <section className="mx-auto flex min-h-screen max-w-4xl items-center justify-center px-4 py-10">
        <div className="rounded-[28px] border border-rose-200 bg-rose-50 px-8 py-10 text-center shadow-[0_20px_70px_-30px_rgba(15,23,42,0.45)]">
          <p className="text-lg font-semibold text-rose-700">{error || 'Otteluparia ei löytynyt.'}</p>
          <button
            type="button"
            onClick={() => navigate(`/tulokset/${competitionID}`)}
            className="mt-5 rounded-full bg-slate-900 px-5 py-2 text-sm font-bold text-white transition hover:bg-slate-800"
          >
            Palaa tuloksiin
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="h-[100dvh] bg-slate-100 px-2 py-2 sm:px-4 sm:py-6 lg:px-6 xl:px-8">
      <div className="mx-auto flex h-full max-w-7xl">
        <div className="flex h-full w-full flex-col rounded-[24px] border border-slate-200 bg-white px-4 py-4 shadow-[0_30px_90px_-40px_rgba(15,23,42,0.45)] sm:rounded-[32px] sm:px-6 sm:py-6 lg:px-8 xl:px-10">
          <div className="flex items-start justify-between gap-3 sm:gap-4">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-cyan-600 sm:text-sm sm:tracking-[0.3em]">Kirjaaminen</p>
              <h1 className="mt-1 text-xl font-black text-slate-900 sm:mt-2 sm:text-3xl md:text-4xl">{competition?.name || 'Ottelun kirjaus'}</h1>
            </div>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="rounded-full border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-100 sm:px-4 sm:py-2 sm:text-sm"
            >
              Sulje
            </button>
          </div>

          {error ? (
            <div className="mt-3 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 sm:mt-5">{error}</div>
          ) : null}

          {showSummary ? (
            <div className="mt-4 space-y-3 overflow-y-auto sm:mt-8 sm:space-y-4">
              <div className="rounded-[24px] border border-emerald-200 bg-emerald-50 px-4 py-4 text-center sm:rounded-[28px] sm:px-6 sm:py-6">
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-emerald-700 sm:text-sm sm:tracking-[0.3em]">Yhteenveto</p>
                <h2 className="mt-2 text-2xl font-black text-slate-900 sm:mt-3 sm:text-3xl">{summaryWinnerText}</h2>
                <p className="mt-2 text-base font-semibold text-slate-700 sm:mt-3 sm:text-lg">{player1Name} {points.player1} - {points.player2} {player2Name}</p>
                {autoConfirmed ? (
                  <p className="mt-3 rounded-2xl border border-amber-200 bg-amber-100 px-4 py-3 text-sm font-semibold text-amber-900">
                    Tulos on automaattivahvistettu {autoConfirmedPlayersLabel || 'järjestelmän toimesta'} {formatDateTime(pair?.autoConfirmedAt)}.
                  </p>
                ) : null}
                <div className="mt-4 flex flex-wrap items-center justify-center gap-2 text-xs font-semibold sm:mt-5 sm:text-sm">
                  <span className={`rounded-full px-3 py-1 ${user1Confirmed ? 'bg-cyan-600 text-white' : 'bg-white text-slate-600'}`}>
                    {player1Name}: {getAutoConfirmedStatusLabel(user1Confirmed, user1AutoConfirmed)}
                  </span>
                  <span className={`rounded-full px-3 py-1 ${user2Confirmed ? 'bg-slate-900 text-white' : 'bg-white text-slate-600'}`}>
                    {player2Name}: {getAutoConfirmedStatusLabel(user2Confirmed, user2AutoConfirmed)}
                  </span>
                </div>
                {pendingScores.length > 0 ? (
                  <p className="mt-4 text-sm font-semibold text-amber-700">Tallentamattomat muutokset pitää lähettää ennen vahvistusta.</p>
                ) : null}
                {editWindowExpired && !bothConfirmed ? (
                  <p className="mt-4 text-sm font-semibold text-slate-600">Muokkausaika on päättynyt, koska ottelun päättymisestä on yli tunti.</p>
                ) : null}
                <div className="mt-5 flex flex-wrap items-center justify-center gap-3 sm:mt-6">
                  <button
                    type="button"
                    onClick={handleConfirmScore}
                    disabled={submitting || bothConfirmed || currentUserConfirmed || pendingScores.length > 0}
                    className="rounded-full bg-emerald-600 px-5 py-2 text-sm font-bold text-white transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {bothConfirmed ? 'Tulos vahvistettu' : currentUserConfirmed ? 'Oma vahvistus tallennettu' : 'Vahvista tulos'}
                  </button>
                  {!bothConfirmed && !currentUserConfirmed && !editWindowExpired ? (
                    <button
                      type="button"
                      onClick={handleEditCompletedResult}
                      disabled={submitting}
                      className="rounded-full border border-slate-300 bg-white px-5 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Muokkaa
                    </button>
                  ) : null}
                </div>
              </div>

              <div className="rounded-[24px] border border-slate-200 bg-slate-50 px-4 py-4 sm:rounded-[28px] sm:px-5 sm:py-5">
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 sm:text-sm sm:tracking-[0.25em]">Väylätulokset</p>
                <div className="mt-3 space-y-2 sm:mt-4">
                  {summaryRows.map((entry) => {
                    const isPlayer1Winner = entry.winnerUserID === player1UserID;
                    const isPlayer2Winner = entry.winnerUserID === player2UserID;

                    return (
                      <div key={`${entry.hole}-${entry.winnerUserID}`} className="rounded-2xl bg-white px-3 py-2.5 text-sm text-slate-700 shadow-sm sm:px-4 sm:py-3">
                        <div className="grid gap-2 sm:grid-cols-[auto_minmax(0,1fr)_auto_minmax(0,1fr)] sm:items-center sm:gap-3">
                          <span className="font-semibold text-slate-900">{entry.holeLabel}</span>
                          <span className={`rounded-md px-2 py-1 font-semibold sm:justify-self-end ${isPlayer1Winner ? 'bg-emerald-200 text-emerald-950' : 'text-slate-700'}`}>
                            {player1Name}
                          </span>
                          <span className="font-bold text-slate-500 sm:justify-self-center">{entry.player1Points} - {entry.player2Points}</span>
                          <span className={`rounded-md px-2 py-1 font-semibold sm:justify-self-start ${isPlayer2Winner ? 'bg-emerald-200 text-emerald-950' : 'text-slate-700'}`}>
                            {player2Name}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-3 flex flex-1 flex-col sm:mt-6">
              <div className="mt-4 shrink-0 rounded-[22px] border border-slate-200 bg-slate-50 px-4 py-3 text-center sm:mt-8 sm:rounded-[28px] sm:px-5 sm:py-6">
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 sm:text-sm sm:tracking-[0.3em]">
                  {selectedHole === 'SD' ? 'Suddendeath' : `Väylä ${selectedHole} / 18`}
                </p>
              </div>

              <div className="mt-3 flex min-h-0 flex-1 flex-col sm:mt-6">
                <div className="flex min-h-0 flex-1 flex-col">
                  <div className="shrink-0 grid gap-2.5 sm:gap-4">
                    <button
                      type="button"
                      disabled={submitting || bothConfirmed || editWindowExpired}
                      onClick={() => handleSetScore(player1UserID)}
                      className={`rounded-[22px] px-4 py-4 text-white shadow-lg transition disabled:cursor-not-allowed disabled:opacity-60 sm:rounded-[28px] sm:px-6 sm:py-8 ${
                        selectedHoleWinnerUserID === player1UserID
                          ? 'border-4 border-cyan-950 bg-cyan-500'
                          : 'border-4 border-transparent bg-cyan-600 hover:bg-cyan-500'
                      }`}
                    >
                      <span className="grid min-h-[3.5rem] grid-cols-[3.5rem_minmax(0,1fr)_3.25rem] items-center gap-2 sm:min-h-[4.5rem] sm:grid-cols-[4.5rem_minmax(0,1fr)_4rem] sm:gap-3">
                        <span className="flex items-center justify-start">
                          {selectedHoleStarterUserID === player1UserID ? (
                            <span className="inline-flex h-11 w-11 items-center justify-center p-0.5 sm:h-14 sm:w-14">
                              <img src={`${process.env.PUBLIC_URL}/disc.png`} alt="Aloittaja" className="h-full w-full object-contain" />
                            </span>
                          ) : (
                            <span className="h-11 w-11 sm:h-14 sm:w-14" aria-hidden="true" />
                          )}
                        </span>
                        <span className="truncate text-center text-lg font-black sm:text-2xl md:text-3xl">
                          {player1Name}
                        </span>
                        <span className="inline-flex min-w-[3.25rem] shrink-0 items-center justify-center rounded-xl border border-white/20 bg-cyan-950/35 px-3 py-2 text-lg font-black shadow-inner shadow-cyan-950/20 sm:min-w-[4rem] sm:text-2xl md:text-3xl">
                          {points.player1}
                        </span>
                      </span>
                    </button>

                    <button
                      type="button"
                      disabled={submitting || bothConfirmed || editWindowExpired || selectedHole === 'SD'}
                      onClick={() => handleSetScore(-1)}
                      className={`w-full rounded-[22px] px-4 py-3.5 text-center text-slate-950 shadow-lg transition disabled:cursor-not-allowed disabled:opacity-50 sm:rounded-[28px] sm:px-6 sm:py-8 ${
                        selectedHoleWinnerUserID === -1
                          ? 'border-4 border-amber-950 bg-amber-300'
                          : 'border-4 border-transparent bg-amber-400 hover:bg-amber-300'
                      }`}
                    >
                      <span className="block text-lg font-black sm:text-2xl md:text-3xl">Tasapeli</span>
                    </button>

                    <button
                      type="button"
                      disabled={submitting || bothConfirmed || editWindowExpired}
                      onClick={() => handleSetScore(player2UserID)}
                      className={`rounded-[22px] px-4 py-4 text-white shadow-lg ring-1 ring-slate-300 transition disabled:cursor-not-allowed disabled:opacity-60 sm:rounded-[28px] sm:px-6 sm:py-8 ${
                        selectedHoleWinnerUserID === player2UserID
                          ? 'border-4 border-cyan-300 bg-slate-800 ring-2 ring-cyan-200'
                          : 'border-4 border-slate-100 bg-slate-900 ring-slate-200 hover:bg-slate-800'
                      }`}
                    >
                      <span className="grid min-h-[3.5rem] grid-cols-[3.5rem_minmax(0,1fr)_3.25rem] items-center gap-2 sm:min-h-[4.5rem] sm:grid-cols-[4.5rem_minmax(0,1fr)_4rem] sm:gap-3">
                        <span className="flex items-center justify-start">
                          {selectedHoleStarterUserID === player2UserID ? (
                            <span className="inline-flex h-11 w-11 items-center justify-center p-0.5 sm:h-14 sm:w-14">
                              <img src={`${process.env.PUBLIC_URL}/disc.png`} alt="Aloittaja" className="h-full w-full object-contain" />
                            </span>
                          ) : (
                            <span className="h-11 w-11 sm:h-14 sm:w-14" aria-hidden="true" />
                          )}
                        </span>
                        <span className="truncate text-center text-lg font-black sm:text-2xl md:text-3xl">
                          {player2Name}
                        </span>
                        <span className="inline-flex min-w-[3.25rem] shrink-0 items-center justify-center rounded-xl border border-white/15 bg-white/10 px-3 py-2 text-lg font-black shadow-inner shadow-black/20 sm:min-w-[4rem] sm:text-2xl md:text-3xl">
                          {points.player2}
                        </span>
                      </span>
                    </button>
                  </div>

                  <div className="mt-auto pt-3 sm:pt-6">
                    <div className="rounded-[22px] border border-slate-200 bg-slate-50 px-3 py-3 sm:rounded-[28px] sm:px-5 sm:py-5">
                      <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 sm:text-sm sm:tracking-[0.25em]">Väylähistoria</p>
                      <div className="mt-3 flex items-stretch gap-1.5 sm:mt-4 sm:gap-2">
                        <div className="min-w-0 flex-1 space-y-1.5 sm:space-y-2">
                          <div className="grid grid-cols-9 gap-1.5 sm:gap-2">
                            {frontNineBadges.map((entry) => {
                              const isSelectedBadge = String(entry.hole) === String(selectedHole);

                              return (
                                <span
                                  key={`history-top-${entry.hole}`}
                                  className={`inline-flex h-7 min-w-0 items-center justify-center rounded-md border px-1 text-[10px] font-black uppercase tracking-[0.08em] sm:h-9 sm:text-xs ${getUnifiedHoleBadgeTone(entry.winnerUserID, player1UserID, player2UserID)} ${
                                    isSelectedBadge ? 'ring-2 ring-cyan-600 ring-offset-2' : ''
                                  }`}
                                >
                                  {entry.hole}
                                </span>
                              );
                            })}
                          </div>
                          <div className="grid grid-cols-9 gap-1.5 sm:gap-2">
                            {backNineBadges.map((entry) => {
                              const isSelectedBadge = String(entry.hole) === String(selectedHole);

                              return (
                                <span
                                  key={`history-bottom-${entry.hole}`}
                                  className={`inline-flex h-7 min-w-0 items-center justify-center rounded-md border px-1 text-[10px] font-black uppercase tracking-[0.08em] sm:h-9 sm:text-xs ${getUnifiedHoleBadgeTone(entry.winnerUserID, player1UserID, player2UserID)} ${
                                    isSelectedBadge ? 'ring-2 ring-cyan-600 ring-offset-2' : ''
                                  }`}
                                >
                                  {entry.hole}
                                </span>
                              );
                            })}
                          </div>
                        </div>

                        {suddenDeathBadge ? (
                          <span
                            className={`inline-flex h-auto min-h-[3.75rem] w-10 shrink-0 items-center justify-center rounded-md border px-1 text-[10px] font-black uppercase tracking-[0.08em] sm:min-h-[5rem] sm:w-12 sm:text-xs ${getUnifiedHoleBadgeTone(suddenDeathBadge.winnerUserID, player1UserID, player2UserID)} ${
                              String(suddenDeathBadge.hole) === String(selectedHole) ? 'ring-2 ring-cyan-600 ring-offset-2' : ''
                            }`}
                          >
                            SD
                          </span>
                        ) : null}
                      </div>

                      <div className="mt-4 grid grid-cols-2 gap-2 sm:mt-6 sm:gap-3">
                        <button
                          type="button"
                          onClick={() => handleArrowNavigation(-1)}
                          disabled={holeSequence.findIndex((hole) => String(hole) === String(selectedHole)) <= 0}
                          className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-2xl border border-slate-700 bg-slate-900 px-3 text-sm font-black uppercase tracking-[0.12em] text-white shadow-lg shadow-slate-950/20 transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-40 sm:h-14 sm:text-base"
                        >
                          <span className="text-xl sm:text-2xl">←</span>
                          <span>Edellinen</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleArrowNavigation(1)}
                          disabled={holeSequence.findIndex((hole) => String(hole) === String(selectedHole)) >= holeSequence.length - 1}
                          className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-2xl border border-cyan-700 bg-gradient-to-r from-cyan-600 to-blue-600 px-3 text-sm font-black uppercase tracking-[0.12em] text-white shadow-lg shadow-cyan-950/20 transition hover:from-cyan-500 hover:to-blue-500 disabled:cursor-not-allowed disabled:opacity-40 sm:h-14 sm:text-base"
                        >
                          <span>Seuraava</span>
                          <span className="text-xl sm:text-2xl">→</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}